
import React, { useMemo } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useAppContext } from '../App';
import { MapPin, Users, UserPlus, UserCheck, Home, User as UserIcon, Clock, ArrowRight } from 'lucide-react';

const SearchResults: React.FC = () => {
    const [searchParams] = useSearchParams();
    const query = searchParams.get('q') || '';
    const { churches, allUsers, currentUser, toggleConnection, outgoingRequests, incomingRequests } = useAppContext();

    const filteredChurches = useMemo(() => {
        if (!query) return [];
        const lowerQ = query.toLowerCase();
        return churches.filter(c => 
            c.name.toLowerCase().includes(lowerQ) || 
            c.country.toLowerCase().includes(lowerQ) ||
            c.region.toLowerCase().includes(lowerQ)
        );
    }, [churches, query]);

    const filteredUsers = useMemo(() => {
        if (!query) return [];
        const lowerQ = query.toLowerCase();
        return allUsers.filter(u => 
            u.name.toLowerCase().includes(lowerQ) ||
            (u.voluntaryCall && u.voluntaryCall.toLowerCase().includes(lowerQ))
        );
    }, [allUsers, query]);

    if (!query) return <div className="p-10 text-center text-gray-500">Please enter a search term in the navigation bar.</div>;

    return (
        <div className="space-y-8 animate-fadeIn">
            <h1 className="text-2xl font-bold text-gray-900">Search Results for "{query}"</h1>

            {/* Churches Section */}
            <div className="space-y-4">
                <h2 className="text-lg font-bold text-gray-800 border-b pb-2 flex items-center gap-2">
                    <Home className="w-5 h-5 text-blue-600"/> Churches ({filteredChurches.length})
                </h2>
                {filteredChurches.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {filteredChurches.map(church => (
                            <div key={church.id} className="bg-white p-4 rounded-lg shadow-sm border border-gray-100 flex items-center gap-4">
                                <img src={church.logoUrl} alt={church.name} className="w-16 h-16 rounded-md object-cover bg-gray-100 flex-shrink-0"/>
                                <div className="min-w-0 flex-1">
                                    <h3 className="font-bold text-gray-900 truncate">{church.name}</h3>
                                    <div className="flex items-center gap-1 text-sm text-gray-500">
                                        <MapPin className="w-3 h-3 flex-shrink-0"/> <span className="truncate">{church.address}, {church.country}</span>
                                    </div>
                                    <div className="mt-2">
                                        <Link to={`/church/${church.id}`} className="text-xs bg-blue-50 text-blue-600 px-3 py-1 rounded font-bold hover:bg-blue-100 transition">
                                            Visit Page
                                        </Link>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <p className="text-gray-500 italic bg-gray-50 p-4 rounded-lg">No churches found matching your query.</p>
                )}
            </div>

            {/* People Section */}
            <div className="space-y-4">
                <h2 className="text-lg font-bold text-gray-800 border-b pb-2 flex items-center gap-2">
                    <UserIcon className="w-5 h-5 text-blue-600"/> People ({filteredUsers.length})
                </h2>
                {filteredUsers.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {filteredUsers.map(user => {
                            const isConnected = currentUser?.connections.includes(user.id);
                            const isPendingOutgoing = outgoingRequests.includes(user.id);
                            const isPendingIncoming = incomingRequests.includes(user.id);
                            
                            const userChurch = churches.find(c => c.id === user.churchId);
                            const isSelf = currentUser?.id === user.id;
                            
                            return (
                                <div key={user.id} className="bg-white p-4 rounded-lg shadow-sm border border-gray-100 flex items-center gap-4">
                                    <img src={user.avatarUrl} alt={user.name} className="w-12 h-12 rounded-full object-cover bg-gray-100 flex-shrink-0"/>
                                    <div className="flex-1 min-w-0">
                                        <h3 className="font-bold text-gray-900 truncate">{user.name} {isSelf && "(You)"}</h3>
                                        <p className="text-xs text-blue-600 font-medium truncate">{user.voluntaryCall || 'Member'}</p>
                                        <p className="text-xs text-gray-500 truncate">{userChurch?.name || 'No Church Selected'}</p>
                                        
                                        {/* Confirmation Message */}
                                        {isPendingOutgoing && (
                                            <p className="text-xs text-green-600 font-bold mt-1 flex items-center gap-1 animate-fadeIn">
                                                <Clock className="w-3 h-3" /> Request Sent
                                            </p>
                                        )}
                                        {isPendingIncoming && (
                                            <p className="text-xs text-orange-600 font-bold mt-1 flex items-center gap-1 animate-fadeIn">
                                                <UserIcon className="w-3 h-3" /> Wants to connect
                                            </p>
                                        )}
                                    </div>
                                    <div className="flex gap-2 items-center">
                                        {currentUser && !isSelf && (
                                            <button 
                                                onClick={() => toggleConnection(user.id)}
                                                className={`p-2 rounded-full transition shadow-sm border ${
                                                    isConnected 
                                                    ? 'bg-white border-green-200 text-green-600' 
                                                    : isPendingOutgoing
                                                    ? 'bg-gray-100 border-gray-200 text-gray-400'
                                                    : 'bg-blue-600 border-transparent text-white hover:bg-blue-700'
                                                }`}
                                                title={isConnected ? "Connected" : isPendingOutgoing ? "Cancel Request" : "Connect"}
                                            >
                                                {isConnected ? <UserCheck className="w-4 h-4"/> : 
                                                 isPendingOutgoing ? <Clock className="w-4 h-4"/> :
                                                 <UserPlus className="w-4 h-4"/>}
                                            </button>
                                        )}
                                        <Link to={`/user/${user.id}`} className="p-2 border border-gray-200 rounded-full hover:bg-gray-50 text-gray-600">
                                            <Users className="w-4 h-4" />
                                        </Link>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    <p className="text-gray-500 italic bg-gray-50 p-4 rounded-lg">No people found matching your query.</p>
                )}
            </div>
        </div>
    );
}

export default SearchResults;
